<template>
  <transition
    :duration="{ enter: 0, leave: 250 }"
    name="modal"
    v-on="transitionListeners"
  >
    <div
      v-show="showModal"
      :id="componentId"
      :aria-labelledby="modalDescription"
      :class="modalClasses"
      role="dialog"
      tabindex="-1"
      @click.self="onClickOutside"
      @keydown.esc="close"
    >
      <div
        :class="dialogClasses"
      >
        <div class="modal-content">
          <div
            v-if="showHeader"
            class="modal-header"
          >
            <div v-if="hasHeading">
              <h4
                v-if="title"
                :id="modalDescription"
                class="modal-title"
              >
                {{ title }}
              </h4>
              <p
                v-if="subtitle"
                class="m-0 text-muted"
              >
                {{ subtitle }}
              </p>
            </div>

            <button
              v-if="showCloseBtn"
              :aria-label="translations.closeButton"
              class="close"
              type="button"
              @click.prevent="close"
            >
              <dp-icon-negative-filled
                color="gray-600"
                :size="24"
              />
            </button>
          </div>

          <div
            ref="modalBody"
            class="modal-body"
          >
            <!-- @slot For modal body -->
            <slot/>
          </div>

          <div
            v-if="hasFooterSlot"
            class="modal-footer"
          >
            <!-- @slot Ideal place for modal action buttons -->
            <slot
              :close="close"
              name="footer"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { DpIconNegativeFilled } from '@dp-ui-kit/icons';
import { debounce } from '@dp-vue/utils';
import { nextTick } from 'vue';

import { SIZE_MD } from '../../config/constants';
import triggerEvent from '../../mixins/trigger-event';
import uidMixin from '../../mixins/uid.mixin';
import { MODAL_EVENT_OPENED, MODAL_SIZES, MODAL_TRANSLATIONS } from './config';

export default {
  name: 'DpModal',
  components: { DpIconNegativeFilled },

  mixins: [
    triggerEvent,
    uidMixin,
  ],

  props: {
    /**
     * Decides if the modal can be closed by clicking browser "back" button.
     */
    closeOnBackButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Decides if the modal can be closed by clicking outside of it.
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * @model modelValue
     */
    modelValue: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Decides whether the close button should be visible. Use with extreme caution.
     */
    showCloseBtn: {
      type: Boolean,
      default: true,
      required: false,
    },
    title: {
      required: false,
      type: String,
      default: '',
    },
    subtitle: {
      required: false,
      type: String,
      default: '',
    },
    /**
     * @values sm, md, lg
     * @default md
     * */
    size: {
      required: false,
      type: String,
      default: SIZE_MD,
      validator: (value) => MODAL_SIZES.includes(value),
    },
    scrollable: {
      required: false,
      type: Boolean,
      default: true,
    },
    stickToBottomOnMobile: {
      required: false,
      type: Boolean,
      default: false,
    },
    scrollLockExceptions: {
      required: false,
      type: String,
      default: '.multiselect, .dropdown-menu',
    },
    /**
     * Object with all necessary translations. You can find a list of required translations in config.js
     * */
    translations: {
      type: Object,
      default: () => MODAL_TRANSLATIONS,
      required: false,
    },
  },

  emits: [
    'update:modelValue',
  ],

  data: () => ({
    showModal: false,
    readyForShowAnimate: false,
    readyForHideAnimate: false,
    clientY: 0,
  }),

  computed: {
    componentId() {
      return `dpModal_${this._uid}`;
    },

    modalDescription() {
      return `modal-title-id${this._uid}`;
    },

    modalClasses() {
      const classes = [
        'modal',
        'fade',
      ];

      if (this.scrollable) {
        classes.push('modal-scrollable');
      }

      if (this.stickToBottomOnMobile) {
        classes.push('modal-stick-bottom-xs');
      }

      if (this.showModal) {
        classes.push('d-block');
      }

      if (this.showModal && this.readyForShowAnimate) {
        classes.push('show');
      }

      return classes;
    },

    dialogClasses() {
      const classes = [
        'modal-dialog',
      ];

      if (this.size !== SIZE_MD) {
        classes.push(`modal-${this.size}`);
      }

      return classes;
    },

    hasFooterSlot() {
      const hasScopedFooterSlot = Object.prototype.hasOwnProperty.call(this.$slots, 'footer');
      const hasFooterSlot = Object.prototype.hasOwnProperty.call(this.$slots, 'footer');

      return hasScopedFooterSlot || hasFooterSlot;
    },

    hasHeading() {
      const { title, subtitle } = this;

      if (!title && !subtitle) {
        return false;
      }

      return title || subtitle;
    },

    showHeader() {
      return this.hasHeading || this.showCloseBtn;
    },

    transitionListeners() {
      if (this.modelValue) {
        this.onAfterEnter();
      }

      return {
        beforeEnter: this.onBeforeEnter,
        afterEnter: this.onAfterEnter,
        afterLeave: this.onAfterLeave,
      };
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        if (typeof oldVal === 'undefined' && !newVal) {
          return;
        }

        this.toggleModal(newVal);
        this.updateHistory(newVal);
      },
    },
  },

  beforeUnmount() {
    if (this.showModal) {
      this.toggleModal(false);
    }
  },

  mounted() {
    this.setUpListeners();
  },

  unmounted() {
    this.tearDownListeners();
  },

  methods: {
    close(event) {
      this.toggleModal(false);
      /**
       * Emitted on modal close
       *
       * @type {boolean}
       */
      this.$emit('update:modelValue', false);

      if (!this.closeOnBackButton) {
        return;
      }

      if (event) {
        window.history.back();
      }
    },

    captureClientY(event) {
      if (!this.showModal) {
        return;
      }

      if (event.targetTouches.length === 1) {
        this.clientY = event.targetTouches[0].clientY;
      }
    },

    onClickOutside(event) {
      if (!this.closeOnClickOutside) {
        return;
      }

      this.close(event);
    },

    getClosesModalId(event) {
      const { target } = event;

      if (!target) {
        return null;
      }

      const closest = target.closest('.modal');

      if (!closest) {
        return null;
      }

      return closest.id;
    },

    preventOverScroll(event) {
      const { scrollLockExceptions, showModal } = this;

      if (!showModal) {
        return;
      }

      if (event.targetTouches.length !== 1) {
        return;
      }

      if (this.componentId !== this.getClosesModalId(event)) {
        return;
      }

      const { modalBody } = this.$refs;
      const clientY = Math.ceil(event.targetTouches[0].clientY - this.clientY);
      const element = event.target.closest(scrollLockExceptions);

      // Allow scrolling only for modal body
      if (!modalBody.contains(event.target)) {
        event.preventDefault();
      }

      // Element is at the top of its scroll, and the user scrolls down
      if (modalBody.scrollTop === 0 && clientY > 0 && !(element instanceof HTMLElement)) {
        event.preventDefault();
      }

      // Element is at the bottom of its scroll, and the user scrolls up
      if (
        (modalBody.scrollHeight - modalBody.scrollTop <= modalBody.clientHeight)
        && clientY < 0
        && !(element instanceof HTMLElement)) {
        event.preventDefault();
      }
    },

    setViewportHeightStyle() {
      const viewportHeight = window.innerHeight * 0.01;

      document.documentElement.style.setProperty('--vh', `${viewportHeight}px`);
    },

    setScrollBarWidthStyle() {
      if (!this.showModal) {
        return;
      }

      const { clientWidth, style } = document.documentElement;

      if (style.cssText.includes('--scroll-bar-width')) {
        return;
      }

      const scrollBarWidth = window.innerWidth - clientWidth;

      style.setProperty('--scroll-bar-width', `${scrollBarWidth}px`);
    },

    toggleBodyClasses() {
      document.body.classList.add('modal-open');

      if (this.readyForHideAnimate) {
        document.body.classList.remove('modal-open');
      }
    },

    toggleModal(value) {
      this.showModal = value;

      let modalCount = 0;

      this.triggerEvent(MODAL_EVENT_OPENED, document, value); // Remove it after fully migration to SSR

      if (value) {
        modalCount += 1;
      } else if (modalCount > 0) {
        modalCount -= 1;
      }

      const modalExists = modalCount > 0;

      if (modalExists) {
        this.setViewportHeightStyle();
        this.setScrollBarWidthStyle();
        nextTick(() => this.focus());
      }
    },

    focus() {
      if (!this.$el) {
        return;
      }

      this.$el.focus();
    },

    updateHistory(value) {
      if (!this.closeOnBackButton) {
        return;
      }

      const { componentId } = this;

      let dpModals = JSON.parse(sessionStorage.getItem('DpModals')) || [];

      if (value) {
        dpModals = [...new Set([...dpModals, componentId])];
        window.history.replaceState({ modalId: componentId }, document.title, document.URL);
        window.history.pushState(null, document.title, document.URL);
      }

      if (!value) {
        dpModals = dpModals.filter((item) => item !== componentId);
      }

      sessionStorage.setItem('DpModals', JSON.stringify(dpModals));
    },

    onPopState(history) {
      const { componentId } = this;
      const { modalId: modalIdFromHistory } = history.state || {};

      if (modalIdFromHistory !== componentId) {
        return;
      }

      const dpModals = JSON.parse(sessionStorage.getItem('DpModals')) || [];
      const [activeModal] = dpModals.filter((item) => item === componentId);

      if (modalIdFromHistory !== activeModal) {
        return;
      }

      this.close();
    },

    onBeforeEnter() {
      this.readyForShowAnimate = false;
      this.readyForHideAnimate = false;
      this.toggleBodyClasses();
    },

    onAfterEnter() {
      this.readyForShowAnimate = true;
    },

    onAfterLeave() {
      this.readyForShowAnimate = false;
      this.readyForHideAnimate = true;
      this.toggleBodyClasses();
    },

    setUpListeners() {
      this.debouncedResize = debounce(() => this.setViewportHeightStyle(), 250);

      document.addEventListener('touchstart', this.captureClientY, { passive: false });
      document.addEventListener('touchmove', this.preventOverScroll, { passive: false });
      window.addEventListener('resize', this.debouncedResize);

      if (this.closeOnBackButton) {
        window.addEventListener('popstate', this.onPopState);
      }
    },

    tearDownListeners() {
      document.removeEventListener('touchstart', this.captureClientY, { passive: false });
      document.removeEventListener('touchmove', this.preventOverScroll, { passive: false });
      window.removeEventListener('resize', this.debouncedResize);

      if (this.closeOnBackButton) {
        window.removeEventListener('popstate', this.onPopState);
      }
    },
  },
};
</script>

<style lang="scss">
  // Vue transition: leave-active
  .modal-leave-active {
    display: block;
    opacity: 0;
  }

  body.modal-open {
    margin-right: var(--scroll-bar-width);
  }
</style>
